import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box, Typography, Grid, Button, Stack, Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, Snackbar, Alert,
} from '@mui/material';
import { useSelector } from 'react-redux';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_DARK } from '../../theme';
import http from '../../Api/http';
import { GetVacancies } from '../../Api/Api';
import { card, solidBtn, ghostBtn, field, ConfirmDialog } from '../uiKit';
import { selectActiveEntityId } from '../../redux/slices/orgSlice';

// There is no categories endpoint yet, so categories are kept in this browser, per entity. Each one holds the
// vacancy names typed under it (e.g. Accounts -> Accountant, Accounts Executive).
const storeKey = (entityId) => `ara.recruitment.categories.${entityId ?? 'all'}`;
const readStore = (entityId) => {
    try { const v = JSON.parse(window.localStorage.getItem(storeKey(entityId)) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
};
const writeStore = (entityId, list) => {
    try { window.localStorage.setItem(storeKey(entityId), JSON.stringify(list)); } catch { /* storage unavailable */ }
};

export const readCategories = readStore;
// vacancy id -> job category name (kept in this browser until the server stores it)
const mapKey = (entityId) => `ara.recruitment.vacancyCategory.${entityId ?? 'all'}`;
export const readVacancyCategories = (entityId) => { try { return JSON.parse(window.localStorage.getItem(mapKey(entityId)) || '{}') || {}; } catch { return {}; } };
export const saveVacancyCategory = (entityId, vacancyId, name) => { try { const m = readVacancyCategories(entityId); if (name) m[vacancyId] = name; else delete m[vacancyId]; window.localStorage.setItem(mapKey(entityId), JSON.stringify(m)); } catch { /* storage unavailable */ } };

const EMPTY = { id: null, name: '', description: '', vacancies: [] };

export default function CategoriesTab({ setActions }) {
    const entityId = useSelector(selectActiveEntityId);
    const [categories, setCategories] = useState([]);
    const [auto, setAuto] = useState([]);
    const [dialog, setDialog] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [tried, setTried] = useState(false);
    const [confirm, setConfirm] = useState(null);
    const [snack, setSnack] = useState(null);
    const notify = (msg, sev = 'success') => setSnack({ msg, sev });

    useEffect(() => { setCategories(readStore(entityId)); }, [entityId]);


    // Every vacancy title becomes a job category on its own.
    useEffect(() => {
        let live = true;
        http.get(GetVacancies).then(({ data: body }) => {
            if (!live) return;
            const list = Array.isArray(body?.data?.vacancies) ? body.data.vacancies : [];
            const by = new Map();
            list.forEach((v) => {
                const name = String(v.roleTitle ?? '').trim();
                if (!name) return;
                const k = name.toLowerCase();
                const e = by.get(k) || { id: `auto-${k}`, name, auto: true, departments: new Set(), count: 0, openings: 0 };
                e.count += 1; e.openings += Number(v.openings) || 0;
                if (v.department) e.departments.add(v.department);
                by.set(k, e);
            });
            setAuto([...by.values()].map((e) => ({ ...e, departments: [...e.departments] })).sort((a, b) => a.name.localeCompare(b.name)));
        }).catch(() => { if (live) setAuto([]); });
        return () => { live = false; };
    }, [entityId]);

    const shown = useMemo(() => {
        const mine = new Set(categories.map((c) => c.name.trim().toLowerCase()));
        return [...categories, ...auto.filter((a) => !mine.has(a.name.toLowerCase()))];
    }, [categories, auto]);

    const persist = (next) => { setCategories(next); writeStore(entityId, next); };

    const openAdd = () => { setForm(EMPTY); setTried(false); setDialog(true); };
    // The Add button lives in the breadcrumb row, like Vacancies; the handler is read through a ref.
    const addRef = useRef();
    addRef.current = openAdd;
    useEffect(() => {
        if (!setActions) return undefined;
        setActions(<Button startIcon={<AddRoundedIcon />} onClick={() => addRef.current()} sx={{ ...solidBtn, height: 42, px: 1.8, fontSize: 13 }}>Add Job Category</Button>);
        return () => setActions(null);
    }, [setActions]);
    const openEdit = (c) => { setForm({ ...EMPTY, ...c }); setTried(false); setDialog(true); };

    const duplicate = useMemo(() => categories.some((c) => c.id !== form.id && c.name.trim().toLowerCase() === form.name.trim().toLowerCase()), [categories, form]);

    const save = () => {
        setTried(true);
        if (!form.name.trim() || duplicate) return;
        const item = { id: form.id || `cat-${Date.now()}`, name: form.name.trim(), description: form.description.trim(), vacancies: form.vacancies };
        persist(form.id ? categories.map((c) => (c.id === form.id ? item : c)) : [...categories, item]);
        setDialog(false);
        notify(form.id ? 'Job category updated.' : 'Job category added.');
    };

    const remove = (c) => { setConfirm(null); persist(categories.filter((x) => x.id !== c.id)); notify('Job category removed.'); };

    return (
        <Box sx={{ p: 2 }}>
            {shown.length === 0 ? null : (
                <Box sx={{ ...card, p: 0, overflow: 'hidden' }}>
                    <Stack direction="row" sx={{ alignItems: 'center', gap: 1.2, px: 2, py: 1.4, borderBottom: '1px solid #EEF0F6' }}>
                        <Box sx={{ width: 34, height: 34, borderRadius: '9px', border: '1px solid #E6EAF1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CategoryRoundedIcon sx={{ color: PRIMARY, fontSize: 19 }} />
                        </Box>
                        <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#0F172A' }}>Job Categories</Typography>
                        <Chip size="small" label={shown.length} sx={{ height: 24, fontSize: 11.5, fontWeight: 700, bgcolor: PRIMARY_LIGHT, color: PRIMARY_DARK }} />
                    </Stack>
                <Grid container spacing={1.6} sx={{ p: 2 }}>
                    {shown.map((c) => (
                        <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={c.id}>
                            <Box sx={{ ...card, p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                    <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center', minWidth: 0 }}>
                                        <Box sx={{ width: 36, height: 36, borderRadius: '9px', bgcolor: PRIMARY_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <CategoryRoundedIcon sx={{ color: PRIMARY, fontSize: 19 }} />
                                        </Box>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: '#0F172A' }} noWrap>{c.name}</Typography>
                                            <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>{c.auto ? `${c.count} vacanc${c.count === 1 ? 'y' : 'ies'} · ${c.openings} opening${c.openings === 1 ? '' : 's'}` : 'Job category'}</Typography>
                                            {c.description && <Typography sx={{ fontSize: 11.5, color: '#64748B' }} noWrap>{c.description}</Typography>}
                                        </Box>
                                    </Stack>
                                    {!c.auto && <Box sx={{ flexShrink: 0 }}>
                                        <Tooltip arrow title="Edit"><IconButton size="small" onClick={() => openEdit(c)} sx={{ color: PRIMARY, '&:hover': { bgcolor: PRIMARY_LIGHT } }}><EditRoundedIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                                        <Tooltip arrow title="Delete"><IconButton size="small" onClick={() => setConfirm(c)} sx={{ color: '#98A0AE', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}><DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                                    </Box>}
                                </Stack>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
                </Box>
            )}

            <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>{form.id ? 'Edit Job Category' : 'Add Job Category'}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.2 }}>Name the job category, e.g. Accounts or Engineering.</Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <Stack spacing={2}>
                        <TextField label="Job category name *" size="small" fullWidth value={form.name} placeholder="e.g. Accounts"
                            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                            error={tried && (!form.name.trim() || duplicate)}
                            helperText={tried && duplicate ? 'A category with this name already exists.' : undefined} sx={field} />
                        <TextField label="Description" size="small" fullWidth value={form.description} placeholder="What this category covers (optional)"
                            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} sx={field} />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setDialog(false)} sx={{ ...ghostBtn, height: 40, px: 2 }}>Cancel</Button>
                    <Button onClick={save} sx={{ ...solidBtn, height: 40, px: 2.6 }}>{form.id ? 'Save Changes' : 'Add Job Category'}</Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={Boolean(confirm)} onClose={() => setConfirm(null)} onConfirm={() => remove(confirm)}
                title="Delete this job category?" body={confirm ? `"${confirm.name}" will be removed. The vacancies themselves are not affected.` : ''} confirmLabel="Delete"
            />

            <Snackbar open={Boolean(snack)} autoHideDuration={3200} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack(null)} severity={snack?.sev || 'success'} variant="filled" sx={{ borderRadius: '7px' }}>{snack?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
